import { Container, Sprite, Texture } from 'pixi.js';
import { GradientBgOptions } from '../../../../elements/resource/types/shape.type';
import { BackgroundService } from '../../../store/studio/background/background.service';
import {
  CanvasBackground,
  CanvasBackgroundConfig,
} from '../../canvas-background';
import { _screenX, _screenY } from '../../../../services/canvas/canvas.service';
import {
  GradientDirection,
  GradientStop,
  RectangleElement,
} from '../primitive/rectangle-element';

/**
 * Renders a gradient background using colors from the background service.
 * Supports both linear and radial gradients with multiple color stops.
 */
export class GradientBackground extends CanvasBackground {
  private gradientSprite: Sprite;

  constructor(
    bgService: BackgroundService,
    private readonly options: GradientBgOptions,
    config: CanvasBackgroundConfig = {},
  ) {
    super(bgService, config);
    this.init();
  }

  addToCanvas(): void {
    // this.container.removeChildren();
    // if (this.gradientSprite) {
    //   this.container.addChild(this.gradientSprite);
    // }
  }

  private init() {
    // Get colors from options or from background service
    const colors = this.options.colors?.length
      ? this.options.colors
      : this.bgService.colors;

    if (!colors || colors.length === 0) {
      console.warn('GradientBackground: No colors available');
      return;
    }

    const rectangle = this.createGradientRectangleElement();
    if (rectangle) {
      // console.log('gradientSprite', rectangle);
      // console.log('gradientSprite > quad', rectangle.quad);
      this.container.addChild(rectangle.element);
      rectangle.element.mask = this.screenRectangle.element;
      if (rectangle.quad) {
        this.container.addChild(rectangle.quad);
        rectangle.quad.mask = this.screenRectangle.element;
      }
    }
    // if (sprite) {
    //   this.gradientSprite = sprite;
    //   // this.gradientSprite.mask = this.screenRectangle.element;
    //   this.container.addChild(this.gradientSprite);
    // }
  }

  private createGradientRectangleElement() {
    const w = this.config.isPortrait ? 1080 : _screenX;

    const h = this.config.isPortrait ? 1350 : _screenY;
    try {
      const rectangle = new RectangleElement(this, this.container, {
        width: w,
        height: h,
        position: { x: 0, y: 0 },
        r: 0,
        gradient: true,
        gradientDirection: this.options.direction as GradientDirection,
        // gradientDirection: this.options.type, // -- //
        // gradientColor: this.options.colors, // -- //
        gradientStops: this.options.colors.map((color, i) => {
          return {
            color,
            stop: i / (this.options.colors.length - 1),
          };
        }) as GradientStop[],
      });

      return rectangle;
    } catch (e) {
      console.error('GradientBackground: Failed to create gradient sprite', e);
      return;
    }
  }

  private createMultiColorGradientSprite(colors: string[]): Sprite | null {
    try {
      const canvas = document.createElement('canvas');
      const w = this.config.isPortrait ? 1080 : _screenX;
      const h = this.config.isPortrait ? 1350 : _screenY;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      let gradient: CanvasGradient;

      if (this.options.type === 'radial') {
        gradient = this.createRadialGradient(ctx, colors, w, h);
      } else {
        gradient = this.createLinearGradient(ctx, colors, w, h);
      }

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);

      const texture = Texture.from(canvas);
      const sprite = new Sprite(texture);
      sprite.x = this.config.isPortrait ? 2 : 0;
      sprite.y = this.config.isPortrait ? 2 : 0;
      return sprite;
    } catch (e) {
      console.error('GradientBackground: Failed to create gradient sprite', e);
      return null;
    }
  }

  private createLinearGradient(
    ctx: CanvasRenderingContext2D,
    colors: string[],
    w: number,
    h: number,
  ): CanvasGradient {
    let x0: number, y0: number, x1: number, y1: number;

    if (this.options.start && this.options.end) {
      [x0, y0] = this.options.start;
      [x1, y1] = this.options.end;
    } else if (this.options.angle !== undefined) {
      // Convert angle to gradient coordinates
      const angleRad = (this.options.angle * Math.PI) / 180;
      const cx = w / 2;
      const cy = h / 2;
      const length = Math.sqrt(w * w + h * h) / 2;
      x0 = cx - Math.cos(angleRad) * length;
      y0 = cy - Math.sin(angleRad) * length;
      x1 = cx + Math.cos(angleRad) * length;
      y1 = cy + Math.sin(angleRad) * length;
    } else {
      // Default: diagonal from top-left to bottom-right
      x0 = 0;
      y0 = 0;
      x1 = w;
      y1 = h;
    }

    const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
    this.addColorStops(gradient, colors);
    return gradient;
  }

  private createRadialGradient(
    ctx: CanvasRenderingContext2D,
    colors: string[],
    w: number,
    h: number,
  ): CanvasGradient {
    const [cx, cy] = this.options.center || [w / 2, h / 2];
    const r = this.options.r || Math.max(w, h) / 2;

    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    this.addColorStops(gradient, colors);
    return gradient;
  }

  private addColorStops(gradient: CanvasGradient, colors: string[]) {
    if (this.options.stops && this.options.stops.length === colors.length) {
      // Use provided stops
      colors.forEach((color, i) => {
        gradient.addColorStop(this.options.stops[i], color);
      });
    } else {
      // Distribute colors evenly
      colors.forEach((color, i) => {
        const stop = colors.length === 1 ? 0 : i / (colors.length - 1);
        gradient.addColorStop(stop, color);
      });
    }
  }

  // Static background, no timeline animation
  setStateByTime(_time: number): void {}
}
